import type { EventMap } from './types'

// ========== 事件名常量 ==========

export const Events = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  REGISTERED: 'registered',
  INCOMING_CALL: 'incomingCall',
  CALL_STATE: 'callState',
  ACCEPT: 'accept',
  REJECT: 'reject',
  HANGUP: 'hangup',
  REMOTE_STREAM: 'remoteStream',
  LOCAL_STREAM: 'localStream',
  SFU_JOIN: 'sfuJoin',
  MEDIA_DEGRADED: 'mediaDegraded',
  SFU_CONNECTION_STATE: 'sfuConnectionState',
  ERROR: 'error',
  ACK: 'ack',
} as const

export type EventName = (typeof Events)[keyof typeof Events]

// ========== 类型安全的事件发射器 ==========

export type EventHandler<T> = T extends undefined ? () => void : (data: T) => void

type EventKey = keyof EventMap

export class TypedEventEmitter {
  private listeners = new Map<string, Set<EventHandler<unknown>>>()

  on<K extends EventKey>(event: K, handler: EventHandler<EventMap[K]>): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler as EventHandler<unknown>)
    return this
  }

  off<K extends EventKey>(event: K, handler: EventHandler<EventMap[K]>): this {
    this.listeners.get(event)?.delete(handler as EventHandler<unknown>)
    return this
  }

  once<K extends EventKey>(event: K, handler: EventHandler<EventMap[K]>): this {
    const wrapper = ((data: unknown) => {
      this.off(event, wrapper as EventHandler<EventMap[K]>)
      ;(handler as EventHandler<unknown>)(data)
    }) as EventHandler<EventMap[K]>
    return this.on(event, wrapper)
  }

  protected emit<K extends EventKey>(event: K, data?: EventMap[K]): void {
    const handlers = this.listeners.get(event)
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(data)
        } catch (e) {
          console.error(`[SDK] Error in ${event} handler:`, e)
        }
      }
    }
  }

  removeAllListeners(event?: EventKey): this {
    if (event) {
      this.listeners.delete(event)
    } else {
      this.listeners.clear()
    }
    return this
  }
}
